import React, { useState, useCallback, useEffect } from 'react';
import {
  updateCritiqueLine,
  getCritiqueLineGlDetail,
  refineCritiqueLine,
  createCritiqueNote,
  updateCritiqueNote,
  deleteCritiqueNote,
  createPackageNote,
} from '../../utils/financialAnalystApi';
import './FinancialAnalyst.css';

const formatCurrency = (val) => {
  if (val == null) return '—';
  const num = Number(val);
  const sign = num < 0 ? '-' : '';
  return `${sign}$${Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatPct = (val) => {
  if (val == null) return '—';
  const num = (Number(val) * 100).toFixed(1);
  return `${num > 0 ? '+' : ''}${num}%`;
};

const formatPctRaw = (val) => {
  if (val == null) return '—';
  return `${Number(val).toFixed(1)}%`;
};

const STATUS_LABELS = {
  draft: 'Draft',
  gm_review: 'GM Review',
  regional_review: 'Regional Review',
  approved: 'Approved',
  locked: 'Locked',
};

const getNoteText = (note) => note?.noteText ?? note?.note_text ?? '';
const getNoteAuthor = (note) => note?.authorId ?? note?.author_id ?? 'user';
const getNoteRole = (note) => note?.authorRole ?? note?.author_role ?? 'user';
const getNoteDate = (note) => note?.createdAt ?? note?.created_at;

// Time-ago helper
const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  return `${diffDays}d ago`;
};

// Group critique lines by department
const groupByDepartment = (lines) => {
  const groups = {};
  for (const line of lines) {
    const dept = line.department || 'Other';
    if (!groups[dept]) groups[dept] = [];
    groups[dept].push(line);
  }
  return groups;
};

// ===================== CritiqueLine Row =====================

const CritiqueLineRow = ({ line, isExpanded, onToggle }) => {
  const variancePositive = Number(line.variancePct || 0) > 0;
  const varianceNegative = Number(line.variancePct || 0) < 0;
  const varianceClass = varianceNegative ? 'negative' : variancePositive ? 'positive' : '';
  const noteCount = (line.notes || []).length;

  return (
    <div
      className={`fa-line-row ${isExpanded ? 'expanded' : ''}`}
      onClick={() => onToggle(line.id)}
    >
      <div className="fa-line-flag-cell">
        {line.flagSeverity && <span className={`fa-flag ${line.flagSeverity}`} />}
      </div>
      <div className="fa-line-account-cell">
        <span className="fa-line-account">{line.glAccountName}</span>
        <span className="fa-line-account-code">{line.glAccountCode}</span>
      </div>
      <div className="fa-line-amount">{formatCurrency(line.amountActual)}</div>
      <div className="fa-line-amount">{formatCurrency(line.amountBudget)}</div>
      <div className="fa-line-amount">{formatCurrency(line.varianceBudget)}</div>
      <div className={`fa-line-variance ${varianceClass}`}>
        {formatPct(line.variancePct)}
      </div>
      <div className="fa-line-note-cell" onClick={(e) => { e.stopPropagation(); onToggle(line.id); }}>
        {noteCount > 0 && (
          <span className="fa-note-indicator">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            <span className="fa-note-count">{noteCount}</span>
          </span>
        )}
      </div>
      <div className={`fa-line-chevron ${isExpanded ? 'open' : ''}`}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </div>
    </div>
  );
};

// ===================== GL Detail Table =====================

const GLDetailTable = ({ transactions, accountCode }) => {
  if (!transactions || transactions.length === 0) {
    return <div className="fa-gl-empty">No GL transactions for this period.</div>;
  }

  return (
    <div className="fa-gl-detail-section">
      <div className="fa-gl-detail-label">GL Detail — {accountCode}</div>
      <table className="fa-gl-detail-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Description</th>
            <th className="amount">Debit</th>
            <th className="amount">Credit</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((tx, i) => {
            const amount = Number(tx.transactionAmount || 0);
            const isDebit = amount >= 0;
            return (
              <tr key={tx.id || i}>
                <td>{new Date(tx.transactionDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })}</td>
                <td>{tx.transactionDesc || tx.vendor || '—'}</td>
                <td className="amount">{isDebit ? formatCurrency(Math.abs(amount)) : '—'}</td>
                <td className="amount">{!isDebit ? formatCurrency(Math.abs(amount)) : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ===================== User Notes =====================

const UserNotes = ({ notes, lineId, onNoteAdded, isLocked }) => {
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  const handleAddNote = async () => {
    if (!newNote.trim() || saving) return;
    setSaving(true);
    try {
      await createCritiqueNote(lineId, { noteText: newNote.trim() });
      setNewNote('');
      if (onNoteAdded) onNoteAdded();
    } catch (err) {
      console.error('Failed to add note:', err);
    }
    setSaving(false);
  };

  const handleUpdateNote = async (noteId) => {
    if (!editText.trim()) return;
    try {
      await updateCritiqueNote(noteId, { noteText: editText.trim() });
      setEditingId(null);
      if (onNoteAdded) onNoteAdded();
    } catch (err) {
      console.error('Failed to update note:', err);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await deleteCritiqueNote(noteId);
      if (onNoteAdded) onNoteAdded();
    } catch (err) {
      console.error('Failed to delete note:', err);
    }
  };

  const getInitials = (email) => {
    if (!email) return '?';
    return email.split('@')[0].substring(0, 2).toUpperCase();
  };

  return (
    <div className="fa-notes-section">
      <div className="fa-notes-label">Notes</div>
      {notes && notes.map((note) => {
        const noteText = getNoteText(note);
        const noteAuthor = getNoteAuthor(note);
        const noteDate = getNoteDate(note);
        return (
        <div className="fa-note-card" key={note.id}>
          <div className="fa-note-avatar">{getInitials(noteAuthor)}</div>
          <div className="fa-note-body">
            <div className="fa-note-meta">
              {noteAuthor} · {getNoteRole(note)} · {noteDate ? new Date(noteDate).toLocaleDateString() : ''}
            </div>
            {editingId === note.id ? (
              <div className="fa-note-input-row">
                <input
                  className="fa-note-input"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleUpdateNote(note.id)}
                />
                <button className="fa-btn fa-btn-sm fa-btn-primary" onClick={() => handleUpdateNote(note.id)}>Save</button>
                <button className="fa-btn fa-btn-sm" onClick={() => setEditingId(null)}>Cancel</button>
              </div>
            ) : (
              <div className="fa-note-text">{noteText}</div>
            )}
          </div>
          {!isLocked && editingId !== note.id && (
            <div className="fa-note-actions">
              <button className="fa-btn fa-btn-sm fa-btn-ghost" onClick={() => { setEditingId(note.id); setEditText(noteText); }}>Edit</button>
              <button className="fa-btn fa-btn-sm fa-btn-ghost" onClick={() => handleDeleteNote(note.id)}>Del</button>
            </div>
          )}
        </div>
        );
      })}
      {!isLocked && (
        <div className="fa-note-input-row">
          <input
            className="fa-note-input"
            placeholder="Add your note..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
          />
          <button className="fa-btn fa-btn-sm fa-btn-primary" onClick={handleAddNote} disabled={saving}>
            {saving ? '...' : 'Save Note'}
          </button>
        </div>
      )}
    </div>
  );
};

// ===================== Critique Detail Panel =====================

const CritiqueDetail = ({ line, onNoteAdded, isLocked, departmentNotes }) => {
  const [glDetail, setGlDetail] = useState(null);
  const [loadingGl, setLoadingGl] = useState(false);
  const [refining, setRefining] = useState(false);
  const lineNotes = line.notes || [];
  const hasLineNotes = lineNotes.length > 0;
  const hasDepartmentNotes = (departmentNotes || []).length > 0;

  const loadGlDetail = useCallback(async () => {
    if (glDetail) return;
    setLoadingGl(true);
    try {
      const data = await getCritiqueLineGlDetail(line.id);
      setGlDetail(data.transactions || []);
    } catch (err) {
      console.error('Failed to load GL detail:', err);
      setGlDetail([]);
    }
    setLoadingGl(false);
  }, [line.id, glDetail]);

  React.useEffect(() => { loadGlDetail(); }, [loadGlDetail]);

  const handleRefine = async () => {
    setRefining(true);
    try {
      const instruction = hasLineNotes || hasDepartmentNotes
        ? null
        : 'Clean up the current analysis for clarity and professional tone. Preserve all specific numbers and do not add unsupported assumptions.';
      await refineCritiqueLine(line.id, instruction);
      if (onNoteAdded) onNoteAdded();
    } catch (err) {
      console.error('Failed to refine:', err);
    }
    setRefining(false);
  };

  return (
    <div className="fa-detail-panel">
      {line.aiNarrative && (
        <div className="fa-ai-analysis">
          <div className="fa-ai-analysis-label">AI Analysis</div>
          <div className="fa-ai-analysis-text">{line.aiNarrative}</div>
        </div>
      )}

      {loadingGl ? (
        <div className="fa-skeleton" style={{ height: 60, width: '100%' }} />
      ) : (
        <GLDetailTable transactions={glDetail} accountCode={line.glAccountCode} />
      )}

      <UserNotes
        notes={lineNotes}
        lineId={line.id}
        onNoteAdded={onNoteAdded}
        isLocked={isLocked}
      />

      {hasDepartmentNotes && (
        <div className="fa-line-dept-notes">
          <div className="fa-notes-label">Department Context</div>
          {departmentNotes.map((note) => (
            <div className="fa-line-dept-note" key={note.id}>
              <strong>{getNoteAuthor(note)?.split('@')[0] || 'user'}</strong>
              <span>{getNoteText(note)}</span>
            </div>
          ))}
        </div>
      )}

      {!isLocked && (
        <div className="fa-refine-bar">
          <div className="fa-refine-bar-label">
            {hasLineNotes || hasDepartmentNotes
              ? 'AI can incorporate line and department notes into this analysis'
              : 'AI can clean up this analysis for clarity'}
          </div>
          <button className="fa-btn fa-btn-sm fa-btn-primary" onClick={handleRefine} disabled={refining}>
            {refining ? 'Working...' : (hasLineNotes || hasDepartmentNotes ? 'AI Refine' : 'AI Cleanup')}
          </button>
        </div>
      )}
    </div>
  );
};

// ===================== Department Note Editor =====================

const DeptNoteEditor = ({ department, notes, packageId, onNoteAdded, isLocked }) => {
  const existingText = (notes || []).map(getNoteText).filter(Boolean).join('\n');
  const [newNote, setNewNote] = useState(existingText);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setNewNote(existingText);
  }, [existingText]);

  const handleAdd = async () => {
    if (!newNote.trim() || saving) return;
    setSaving(true);
    try {
      await createPackageNote(packageId, { noteText: newNote.trim(), department });
      if (onNoteAdded) onNoteAdded();
    } catch (err) {
      console.error('Failed to add dept note:', err);
    }
    setSaving(false);
  };

  return (
    <div className="fa-dept-note-editor">
      {notes && notes.map((note) => (
        <div className="fa-dept-note-item" key={note.id}>
          <span className="fa-dept-note-author">{getNoteAuthor(note)?.split('@')[0]}</span>
          <span className="fa-dept-note-text">{getNoteText(note)}</span>
          <span className="fa-dept-note-time">{getNoteDate(note) ? new Date(getNoteDate(note)).toLocaleDateString() : ''}</span>
        </div>
      ))}
      {!isLocked && (
        <div className="fa-dept-note-input-row">
          <textarea
            className="fa-note-input"
            placeholder={`Add department context for ${department}...`}
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            rows={2}
          />
          <button className="fa-btn fa-btn-sm fa-btn-primary" onClick={handleAdd} disabled={saving}>
            {saving ? '...' : 'Save Department Note'}
          </button>
        </div>
      )}
    </div>
  );
};

// ===================== Main CritiqueCard =====================

const CritiqueCard = ({ packageData, critiqueLines, departmentNotes, packageId, onRefresh, onExport, onSave, saving }) => {
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [showDeptNote, setShowDeptNote] = useState({});
  const isLocked = packageData?.status === 'locked';

  const toggleDeptNote = (dept) => {
    setShowDeptNote(prev => ({ ...prev, [dept]: !prev[dept] }));
  };

  const toggleLine = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const groups = groupByDepartment(critiqueLines || []);

  // Compute KPI metrics
  const allLines = critiqueLines || [];
  const criticalCount = allLines.filter(l => l.flagSeverity === 'red').length;
  const attentionCount = allLines.filter(l => l.flagSeverity === 'yellow').length;
  const favorableCount = allLines.filter(l => l.flagSeverity === 'green').length;
  const flaggedCount = criticalCount + attentionCount;

  const totalRevenue = allLines
    .filter(l => (l.department || '').toLowerCase().includes('revenue') || (l.glAccountName || '').toLowerCase().includes('revenue'))
    .reduce((sum, l) => sum + Number(l.amountActual || 0), 0);
  const totalRevenueBudget = allLines
    .filter(l => (l.department || '').toLowerCase().includes('revenue') || (l.glAccountName || '').toLowerCase().includes('revenue'))
    .reduce((sum, l) => sum + Number(l.amountBudget || 0), 0);
  const revenueVarPct = totalRevenueBudget ? ((totalRevenue - totalRevenueBudget) / totalRevenueBudget * 100).toFixed(1) : 0;

  const totalActual = allLines.reduce((sum, l) => sum + Number(l.amountActual || 0), 0);
  const totalBudget = allLines.reduce((sum, l) => sum + Number(l.amountBudget || 0), 0);
  const gopVarPct = totalBudget ? ((totalActual - totalBudget) / totalBudget * 100).toFixed(1) : 0;

  // Flow-through: GOP / Revenue
  const flowThrough = totalRevenue ? ((totalActual / totalRevenue) * 100).toFixed(1) : 0;
  const flowThroughBudget = totalRevenueBudget ? ((totalBudget / totalRevenueBudget) * 100).toFixed(1) : 0;

  // Format period
  const periodLabel = packageData?.periodStart
    ? new Date(packageData.periodStart).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    : '';

  const periodRange = packageData?.periodStart && packageData?.periodEnd
    ? `${new Date(packageData.periodStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}–${new Date(packageData.periodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    : '';

  return (
    <div className="fa-critique-card">
      {/* Header */}
      <div className="fa-critique-header">
        <div className="fa-critique-header-left">
          <span className="fa-critique-badge">AI Critique</span>
          <div>
            <div className="fa-critique-title">P&L Line-by-Line Review</div>
            <span className="fa-critique-period">· {periodLabel}</span>
          </div>
        </div>
        <div className="fa-critique-header-actions">
          {onExport && (
            <button className="fa-btn fa-btn-sm" onClick={onExport}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Export
            </button>
          )}
          {onSave && !isLocked && (
            <button className="fa-btn fa-btn-sm fa-btn-primary" onClick={onSave} disabled={saving}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
              {saving ? 'Submitting...' : 'Submit for Review'}
            </button>
          )}
        </div>
      </div>

      {/* Status Bar */}
      <div className="fa-status-bar">
        <span className={`fa-status-pill ${packageData?.status || 'draft'}`}>
          <span className="fa-status-dot" />
          {STATUS_LABELS[packageData?.status] || 'Draft'}
        </span>
        {periodRange && (
          <span className="fa-status-meta">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            Period: {periodRange}
          </span>
        )}
        {packageData?.createdBy && (
          <span className="fa-status-meta">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            {packageData.createdBy}
          </span>
        )}
        {packageData?.createdAt && (
          <span className="fa-status-meta">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            Generated {timeAgo(packageData.createdAt)}
          </span>
        )}
      </div>

      {/* Stats */}
      <div className="fa-stats-row">
        <div className="fa-stat-card">
          <div className="fa-stat-label">Flags</div>
          <div className="fa-stat-value fa-stat-flags">{flaggedCount}</div>
          <div className="fa-stat-sub">{criticalCount} critical · {attentionCount} attention · {favorableCount} favorable</div>
        </div>
        <div className="fa-stat-card">
          <div className="fa-stat-label">Total Revenue</div>
          <div className="fa-stat-value fa-stat-green">{formatCurrency(totalRevenue)}</div>
          <div className="fa-stat-sub">{revenueVarPct > 0 ? '+' : ''}{revenueVarPct}% vs budget</div>
        </div>
        <div className="fa-stat-card">
          <div className="fa-stat-label">GOP</div>
          <div className="fa-stat-value fa-stat-blue">{formatCurrency(totalActual)}</div>
          <div className="fa-stat-sub">{gopVarPct > 0 ? '+' : ''}{gopVarPct}% vs budget</div>
        </div>
        <div className="fa-stat-card">
          <div className="fa-stat-label">Flow-Through</div>
          <div className="fa-stat-value fa-stat-green">{flowThrough}%</div>
          <div className="fa-stat-sub">vs {flowThroughBudget}% budget</div>
        </div>
      </div>

      {/* Line Items Table Header */}
      <div className="fa-line-header">
        <div></div>
        <div>Account</div>
        <div className="fa-line-amount">Actual</div>
        <div className="fa-line-amount">Budget</div>
        <div className="fa-line-amount">Variance</div>
        <div className="fa-line-amount">Var %</div>
        <div></div>
        <div></div>
      </div>

      {/* Line Items by Department */}
      <div className="fa-lines-container">
        {Object.entries(groups).map(([dept, lines]) => {
          const deptNotes = departmentNotes?.[dept] || [];
          return (
          <div key={dept}>
            <div className="fa-dept-header">
              <span>{dept.replace(/_/g, ' ')}</span>
              <button
                type="button"
                className="fa-dept-note-icon"
                onClick={(e) => { e.stopPropagation(); toggleDeptNote(dept); }}
                title={deptNotes.length > 0 ? `${deptNotes.length} note(s)` : 'Add note'}
              >
                {deptNotes.length > 0 ? (
                  <span className="fa-dept-note-badge">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                    {deptNotes.length}
                  </span>
                ) : (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Dept note
                  </>
                )}
              </button>
            </div>
            {showDeptNote[dept] && (
              <DeptNoteEditor
                department={dept}
                notes={deptNotes}
                packageId={packageId}
                onNoteAdded={onRefresh}
                isLocked={isLocked}
              />
            )}
            {lines.map((line) => (
              <React.Fragment key={line.id}>
                <CritiqueLineRow
                  line={line}
                  isExpanded={expandedIds.has(line.id)}
                  onToggle={toggleLine}
                />
                {expandedIds.has(line.id) && (
                  <CritiqueDetail
                    line={line}
                    onNoteAdded={onRefresh}
                    isLocked={isLocked}
                    departmentNotes={deptNotes}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default CritiqueCard;
// Reused by the Narrative Studio commentary rows on the financial report —
// the per-line panel (AI analysis + GL detail + notes + AI refine) unchanged.
export { CritiqueDetail };
